"use client"

import { EngineGroup, FinishLevel, PackageOption } from "@eurodrive/utils"
import { Box } from "@mui/material"
import { LeasingCarDto, SaleCarDto } from "api"
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react"
import { Config, EngineOption, StepConfig } from "./CarConfiguration"
import FinishStage from "./FinishStage"
import FirstStage from "./FirstStage"
import PackagesStage from "./PackagesStage"
import SecondStage from "./SecondStage"
import ThirdStage from "./ThirdStage"
import TrimStage from "./TrimStage"

function StepPanel({
  active,
  children,
}: {
  active: boolean
  children: React.ReactNode
}) {
  if (!active) return null
  return <Box>{children}</Box>
}

interface UseCarConfigurationProps {
  config: Config
  setConfig: Dispatch<SetStateAction<Config>>
  data: SaleCarDto | LeasingCarDto
  options: EngineOption[]
  engineGroups?: EngineGroup[]
  colors: { color: string; label: string; price: number }[]
  finishLevels?: FinishLevel[]
  additionalPackages?: PackageOption[]
  isLeasing?: boolean
}

interface UseCarConfigurationReturn {
  activeStep: number
  completed: boolean[]
  setActiveStep: Dispatch<SetStateAction<number>>
  markComplete: (index: number) => void
  clickStep: (index: number) => void
  isStepDisabled: (index: number) => boolean
  steps: StepConfig[]
}

export function useCarConfiguration({
  config,
  setConfig,
  data,
  options,
  engineGroups = [],
  colors,
  finishLevels = [],
  additionalPackages = [],
  isLeasing = false,
}: UseCarConfigurationProps): UseCarConfigurationReturn {
  const hasColors = colors && colors.length > 0
  const hasPackageOptions = additionalPackages.length > 0

  // For sales, get the selected engine group for trim selection
  const selectedEngineGroup =
    !isLeasing && engineGroups.length > 0 && config.engine !== null
      ? engineGroups[config.engine]
      : null
  // Check if there are any engine groups available for sales flow
  const hasEngineGroups = !isLeasing && engineGroups.length > 0

  const steps: StepConfig[] = useMemo(() => {
    if (isLeasing) {
      // Leasing flow: Engine → [Finish if available] → Color → Packages → Summary
      const hasFinishLevels = finishLevels.length > 0
      const leasingSteps = [
        {
          key: "model",
          label: "בחירת מנוע",
          render: ({ markComplete }: { markComplete: () => void }) => (
            <FirstStage
              markComplete={markComplete}
              options={options}
              config={config}
              setConfig={setConfig}
              isLeasing={true}
            />
          ),
        },
        ...(hasFinishLevels
          ? [
              {
                key: "finish",
                label: "בחירת גימור",
                render: ({ markComplete }: { markComplete: () => void }) => (
                  <FinishStage
                    markComplete={markComplete}
                    finishLevels={finishLevels}
                    config={config}
                    setConfig={setConfig}
                  />
                ),
              },
            ]
          : []),
        ...(hasColors
          ? [
              {
                key: "color",
                label: "בחירת צבע",
                render: ({ markComplete }: { markComplete: () => void }) => (
                  <SecondStage
                    markComplete={markComplete}
                    colors={colors}
                    config={config}
                    setConfig={setConfig}
                  />
                ),
              },
            ]
          : []),
        ...(hasPackageOptions
          ? [
              {
                key: "packages",
                label: "בחירת חבילות",
                render: ({ markComplete }: { markComplete: () => void }) => (
                  <PackagesStage
                    markComplete={markComplete}
                    additionalPackages={additionalPackages}
                    config={config}
                    setConfig={setConfig}
                  />
                ),
              },
            ]
          : []),
        {
          key: "summary",
          label: "סיכום הזמנה",
          render: () => (
            <ThirdStage
              colors={colors}
              config={config}
              options={options}
              engineGroups={engineGroups}
              data={data}
              hasColors={hasColors}
              finishLevels={finishLevels}
              additionalPackages={additionalPackages}
              isLeasing={true}
            />
          ),
        },
      ]

      return leasingSteps
    } else {
      // Sale flow: Engine → [Trim if multiple] → Color → Summary
      const saleSteps: StepConfig[] = [
        {
          key: "model",
          label: "בחירת מנוע",
          render: ({ markComplete }: { markComplete: () => void }) => (
            <FirstStage
              markComplete={markComplete}
              options={options}
              engineGroups={engineGroups}
              config={config}
              setConfig={setConfig}
              isLeasing={false}
            />
          ),
        },
      ]

      // Always add trim step for sales flow when engine groups are available
      if (hasEngineGroups) {
        saleSteps.push({
          key: "trim",
          label: "בחירת רמת גימור",
          render: ({ markComplete }: { markComplete: () => void }) => (
            <TrimStage
              markComplete={markComplete}
              trims={selectedEngineGroup?.trims || []}
              config={config}
              setConfig={setConfig}
            />
          ),
        })
      }

      // Add color step if colors exist
      if (hasColors) {
        saleSteps.push({
          key: "color",
          label: "בחירת צבע",
          render: ({ markComplete }: { markComplete: () => void }) => (
            <SecondStage
              markComplete={markComplete}
              colors={colors}
              config={config}
              setConfig={setConfig}
            />
          ),
        })
      }

      // Add summary step
      saleSteps.push({
        key: "summary",
        label: "סיכום הזמנה",
        render: () => (
          <ThirdStage
            colors={colors}
            config={config}
            options={options}
            engineGroups={engineGroups}
            data={data}
            hasColors={hasColors}
            finishLevels={[]}
            additionalPackages={[]}
            isLeasing={false}
          />
        ),
      })

      return saleSteps
    }
  }, [
    config,
    setConfig,
    data,
    options,
    engineGroups,
    colors,
    hasColors,
    hasEngineGroups,
    selectedEngineGroup,
    finishLevels,
    additionalPackages,
    hasPackageOptions,
    isLeasing,
  ])

  const [activeStep, setActiveStep] = useState(0)
  const [completed, setCompleted] = useState<boolean[]>(() =>
    Array(steps.length).fill(false),
  )

  const markComplete = useCallback((index: number) => {
    setCompleted((prev) => {
      if (prev[index]) return prev
      const next = [...prev]
      next[index] = true
      return next
    })
  }, [])

  // The trim and color steps derive their completion from config rather than
  // the one-way completed flags:
  // - trim: the engine step pre-selects the cheapest trim, so the step is
  //   already satisfied on arrival. Deriving it keeps the continue button in
  //   sync with the card that already renders as selected (END-94)
  // - color: changing engine/trim resets the color choice, which must re-lock
  //   the steps after it
  const effectiveCompleted = useMemo(
    () =>
      steps.map((step, index) => {
        if (step.key === "trim") {
          return (
            config.trim !== null &&
            Boolean(selectedEngineGroup?.trims[config.trim])
          )
        }
        if (step.key === "color") {
          return config.color !== null && Boolean(colors[config.color])
        }
        return completed[index] || false
      }),
    [steps, completed, config.trim, config.color, colors, selectedEngineGroup],
  )

  const isStepComplete = useCallback(
    (index: number) => {
      const step = steps[index]

      if (step?.key === "packages") {
        return (
          config.packages !== null &&
          config.packages !== undefined &&
          Boolean(additionalPackages[config.packages])
        )
      }

      return effectiveCompleted[index] || false
    },
    [additionalPackages, effectiveCompleted, config.packages, steps],
  )

  const isStepDisabled = useCallback(
    (index: number) => {
      // Disabled if any previous step is not complete.
      for (let i = 0; i < index; i++) {
        if (!isStepComplete(i)) return true
      }
      return false
    },
    [isStepComplete],
  )

  const clickStep = useCallback(
    (index: number) => {
      // All previous must be completed.
      if (isStepDisabled(index)) return
      setActiveStep(index)
    },
    [isStepDisabled],
  )

  return {
    activeStep,
    completed: effectiveCompleted,
    setActiveStep,
    markComplete,
    clickStep,
    isStepDisabled,
    steps,
  }
}

interface ConfigurationStepsProps {
  activeStep: number
  completed: boolean[]
  markComplete: (index: number) => void
  steps: StepConfig[]
}

export default function ConfigurationSteps({
  activeStep,
  completed,
  markComplete,
  steps,
}: ConfigurationStepsProps) {
  return (
    <>
      {steps.map((step, idx) => (
        <StepPanel key={step.key} active={idx === activeStep}>
          {step.render({
            markComplete: () => markComplete(idx),
            completed: completed[idx],
            active: idx === activeStep,
          })}
        </StepPanel>
      ))}
    </>
  )
}
